import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useAuth } from '../contexts/AuthContext';
import { useShop } from '../contexts/ShopContext';
import { useColorMode } from '../App';
import { UserRole } from '../types';
import { radius } from '../theme/tokens';
import { adminNavSections, isNavActive } from './navigation';

export const SIDEBAR_WIDTH = 248;

const initialsOf = (nameOrEmail: string) =>
  nameOrEmail
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');

/** Everything inside the sidebar; shared by the fixed desktop panel and the mobile drawer. */
const SidebarContent: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const { selectedShop } = useShop();
  const { mode, toggleColorMode } = useColorMode();
  const location = useLocation();
  const sections = adminNavSections(user?.role);

  const displayName = user?.name || user?.email || '';
  const roleLabel = user?.role === UserRole.SUPER_ADMIN ? 'Super admin' : 'Shop admin';

  return (
    <Stack sx={{ height: '100%', px: 1.75, pt: 2.5, pb: 1.5 }} spacing={3}>
      {/* Brand + current shop */}
      <Box
        component={RouterLink}
        to="/admin/dashboard"
        onClick={onNavigate}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1, color: 'inherit', textDecoration: 'none' }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            borderRadius: `${radius.sm}px`,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <BuildCircleIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', letterSpacing: '-0.015em', lineHeight: 1.2 }}>
            MechaniX
          </Typography>
          {selectedShop && (
            <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ lineHeight: 1.3 }}>
              {selectedShop.name}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Navigation */}
      <Box component="nav" aria-label="Admin" sx={{ flex: 1, overflowY: 'auto', mx: -0.5, px: 0.5 }}>
        {sections.map((section) => (
          <Box key={section.title} sx={{ '& + &': { mt: 2 } }}>
            <Typography
              variant="overline"
              color="text.secondary"
              component="div"
              sx={{ px: 1.25, pb: 0.5, fontSize: '0.6875rem' }}
            >
              {section.title}
            </Typography>
            <List disablePadding>
              {section.items.map((item) => {
                const active = isNavActive(item.to, location.pathname);
                const Icon = item.icon;
                return (
                  <ListItemButton
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    sx={{
                      borderRadius: `${radius.sm}px`,
                      px: 1.25,
                      py: 0.875,
                      mb: 0.25,
                      color: active ? 'text.primary' : 'text.secondary',
                      bgcolor: (t) =>
                        active ? alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.1 : 0.16) : 'transparent',
                      '&:hover': {
                        bgcolor: (t) =>
                          active
                            ? alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.14 : 0.22)
                            : t.palette.action.hover,
                        color: 'text.primary',
                      },
                    }}
                  >
                    {Icon && (
                      <ListItemIcon sx={{ minWidth: 32, color: active ? 'primary.main' : 'inherit' }}>
                        <Icon fontSize="small" />
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={item.label}
                      slotProps={{
                        primary: { sx: { fontSize: '0.875rem', fontWeight: active ? 600 : 500 } },
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Signed-in user */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ px: 1, pt: 1.5, borderTop: 1, borderColor: 'divider' }}
      >
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: '0.8125rem',
            fontWeight: 700,
            bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.12 : 0.2),
            color: 'primary.main',
          }}
        >
          {initialsOf(displayName) || '?'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.3 }}>
            {displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ lineHeight: 1.3 }}>
            {roleLabel}
          </Typography>
        </Box>
        <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
          <IconButton size="small" onClick={toggleColorMode} aria-label="Toggle dark and light mode">
            {mode === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Sign out">
          <IconButton size="small" onClick={logout} aria-label="Sign out">
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

/**
 * The admin shell: a fixed sidebar on desktop, and on phones a slim top bar
 * whose menu button slides the same sidebar out. Public pages and the mechanic
 * schedule keep the top Navbar (see AppChrome).
 */
const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { selectedShop } = useShop();
  const close = React.useCallback(() => setMobileOpen(false), []);

  const paperSx = {
    width: SIDEBAR_WIDTH,
    boxSizing: 'border-box',
    bgcolor: 'background.paper',
    borderRight: 1,
    borderColor: 'divider',
    backgroundImage: 'none',
  } as const;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="permanent"
        open
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': paperSx,
        }}
      >
        <SidebarContent />
      </Drawer>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={close}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': paperSx }}
      >
        <SidebarContent onNavigate={close} />
      </Drawer>

      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="sticky" sx={{ display: { xs: 'block', md: 'none' } }}>
          <Toolbar sx={{ minHeight: 56, gap: 1 }}>
            <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label="Open navigation menu">
              <MenuIcon />
            </IconButton>
            <Typography sx={{ fontWeight: 700, letterSpacing: '-0.015em' }} noWrap>
              {selectedShop?.name ?? 'MechaniX'}
            </Typography>
          </Toolbar>
        </AppBar>
        <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
